"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { SiteNavigation } from "@/components/ui/site-navigation";
import { LandingFooter } from "@/components/ui/footer";
import { PillButton } from "@/components/ui/pill-button";
import { DecodeText } from "@/components/ui/decode-text";
import { ArrowLeft } from "lucide-react";
import dynamic from "next/dynamic";

// Dynamically import parametric components to avoid SSR issues
const CommutativeNetwork = dynamic(
  () =>
    import("@/components/parametric/CommutativeNetworkGLSL").then(
      (mod) => mod.CommutativeNetworkGLSL
    ),
  { ssr: false }
);

export default function NotFound() {
  const router = useRouter();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    document.title = "Symbolica – Page Not Found";
    setMounted(true);
  }, []);

  const handleGoBack = () => {
    if (window.history.length > 1) {
      router.back();
    } else {
      router.push("/");
    }
  };

  return (
    <main className="relative min-h-screen bg-[#FFFDF0] text-[#0a0a0a] overflow-hidden">
      {/* Subtle grid background */}
      <LightGrid />

      {/* Navigation */}
      <SiteNavigation isDark={false} />

      {/* Main content */}
      <div className="relative z-10 mx-auto max-w-[96rem] px-8 lg:px-24">
        <div className="flex min-h-[calc(100vh-200px)] flex-col lg:flex-row items-center justify-center gap-12 lg:gap-24 py-16">
          {/* Left side - Text content */}
          <div className="relative flex-1 flex flex-col items-start justify-center max-w-xl">
            {/* Backdrop blur container */}
            <div className="relative z-10">
              {/* Label */}
              <div className="font-ibm-plex-mono text-[13px] font-medium uppercase tracking-[0.2em] text-black/50 mb-6">
                <DecodeText text="PATH NOT FOUND" delay={0} />
              </div>

              {/* Large 404 */}
              <div className="relative mb-6">
                <h1 className="font-arizona-mix text-[120px] sm:text-[160px] lg:text-[200px] leading-[0.85] tracking-[-8px] text-[#0a0a0a]/[0.06] select-none">
                  404
                </h1>
                <div className="absolute inset-0 flex items-center">
                  <h2 className="font-arizona-mix text-[42px] sm:text-[52px] lg:text-[60px] leading-[1.1] tracking-[-1.5px] text-[#0a0a0a]">
                    Lost in the
                    <br />
                    <Link
                      href="/research"
                      className="text-periwinkle hover:text-periwinkle/70 transition-colors cursor-pointer"
                    >
                      latent space
                    </Link>
                  </h2>
                </div>
              </div>

              {/* Description */}
              <p className="font-arizona-sans text-[17px] leading-[1.7] text-[#0a0a0a]/70 max-w-md mb-10">
                The path you're looking for has diverged beyond our manifold.
                Perhaps the morphism you seek exists elsewhere in the category.
              </p>

              {/* Separator */}
              <div className="w-full h-px bg-black/10 mb-10" />

              {/* Action buttons */}
              <div className="flex flex-row gap-4">
                <Link href="/">
                  <PillButton variant="outline">Home</PillButton>
                </Link>
                <PillButton
                  variant="outline"
                  onClick={handleGoBack}
                  type="button"
                >
                  Go Back
                </PillButton>
              </div>

              {/* Error code */}
              <div className="mt-10 font-ibm-plex-mono text-[11px] tracking-[0.1em] text-black/30">
                ERROR_CODE: HTTP_404_NOT_FOUND
              </div>
            </div>
          </div>

          {/* Right side - Parametric visualization */}
          <div className="w-full max-w-xl lg:flex-1 lg:max-w-none">
            <div className="relative aspect-square max-h-[400px] lg:max-h-[600px] w-full">
              {/* Visualization container with border */}
              <div className="absolute inset-0 border border-black/10 overflow-hidden bg-[#FFFDF0]">
                {mounted && (
                  <CommutativeNetwork
                    backgroundColor="#FFFDF0"
                    lineColor="#0a0a0a"
                    nodeColor="#7678ED"
                    vertexRadius={0.03}
                    rotationSpeed={0.006}
                    animated
                  />
                )}
              </div>

              {/* Corner accents */}
              <div className="absolute -top-2 -left-2 w-4 h-4 border-l-2 border-t-2 border-black/20" />
              <div className="absolute -top-2 -right-2 w-4 h-4 border-r-2 border-t-2 border-black/20" />
              <div className="absolute -bottom-2 -left-2 w-4 h-4 border-l-2 border-b-2 border-black/20" />
              <div className="absolute -bottom-2 -right-2 w-4 h-4 border-r-2 border-b-2 border-black/20" />
            </div>

            {/* Caption under visualization */}
            <div className="mt-6 text-center lg:text-left">
              <p className="font-ibm-plex-mono text-[11px] tracking-[0.15em] text-black/40 uppercase">
                Categorical visualization of missing endpoints
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Suggested links section */}
      <SuggestedLinksSection />

      {/* Footer */}
      <LandingFooter
        colorMode="gray"
        noMargin
        className="max-w-[96rem] lg:max-w-[96rem] xl:max-w-[96rem] px-8 sm:px-8 lg:px-24 xl:px-24"
      />
    </main>
  );
}

function SuggestedLinksSection() {
  const links = [
    {
      title: "Research",
      description: "Explore our approach to neural-symbolic AI",
      href: "/research",
    },
    {
      title: "Agentica",
      description: "Try our agent builder",
      href: "/agentica",
    },
    {
      title: "Careers",
      description: "Join our team in SF or London",
      href: "/careers",
    },
  ];

  return (
    <section className="relative z-10 border-t border-black/10 bg-[#F5F3E7]/50">
      <div className="mx-auto max-w-[96rem] px-8 py-20 lg:px-24">
        <div className="font-ibm-plex-mono text-[13px] font-medium uppercase tracking-[0.2em] text-black/50 mb-8">
          <DecodeText text="SUGGESTED DESTINATIONS" delay={300} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {links.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className="group relative p-6 border border-black/10 bg-[#FFFDF0] hover:bg-white hover:border-black/20 transition-all duration-300"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-arizona-mix text-[20px] tracking-[-0.5px] text-[#0a0a0a] group-hover:text-periwinkle transition-colors">
                    {link.title}
                  </h3>
                  <p className="font-arizona-sans text-[14px] text-[#0a0a0a]/60 mt-1">
                    {link.description}
                  </p>
                </div>
                <ArrowLeft className="w-4 h-4 text-black/30 rotate-180 group-hover:text-periwinkle group-hover:translate-x-1 transition-all" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function LightGrid() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 bg-[#FFFDF0]" />
      <div className="absolute inset-0 opacity-[0.08]">
        <GridOverlay opacity={0.1} lineTone="rgba(0,0,0,0.06)" size={72} />
      </div>
    </div>
  );
}

function GridOverlay({
  opacity = 0.2,
  lineTone = "rgba(0,0,0,0.08)",
  size = 64,
}: {
  opacity?: number;
  lineTone?: string;
  size?: number;
}) {
  return (
    <div
      className="size-full"
      style={{
        backgroundImage: `linear-gradient(${lineTone} 1px, transparent 1px), linear-gradient(90deg, ${lineTone} 1px, transparent 1px)`,
        backgroundSize: `${size}px ${size}px`,
        opacity,
      }}
    />
  );
}
